import { Alert, Badge, Grid, useMantineTheme } from '@mantine/core'
import { selectedPropertyHasPermission } from 'App/Map/PanelDetails/_utils/selectedPropertyHasPermission'
import { transformCoordsToLatLng } from 'App/ServiceCenter/Map/_utils/transformCoordsToLatLng'
import { openNodeInstallationModal } from 'App/ServiceCenter/NodeInstallation/NodeInstallation'
import { networkStore } from 'App/ServiceCenter/store/networkStore'
import type { TManagementTypes, TPlannedGateway, TPlannedNode } from 'App/ServiceCenter/types'
import { TNodeStatus } from 'App/ServiceCenter/types'
import { TRAP_NODE_TYPES } from 'App/ServiceCenter/utils/constants/nodeType'
import { Button } from 'components/Button/Button'
import { IconWarning } from 'components/icons/IconWarning'
import { MapCard } from 'components/MapCard/MapCard'
import type { Point } from 'geojson'
import { translate } from 'i18n/i18n'
import React, { useMemo } from 'react'
import { colors } from 'settings/colors'
import { layersNavigationMenuStore } from 'stores/layersNavigationMenuStore/layersNavigationMenuStore'
import { useIsWideScreen } from 'utils/useIsWideScreen'
import type { GeoJSONPoint } from 'wellknown'
import { openActionMenu } from '../../ActionMenu/ActionMenu'
import { getEquipmentTypeLabel } from '../../Map/_utils/equipmentTypes'
import { getNodeStatusLabel, NODE_STATUS_COLORS } from '../../Map/_utils/getActiveNodeStatus'
import { getNodeEquipmentType } from '../../Map/_utils/getNodeEquipmentType'
import { apiRequestQueueStore } from '../../utils/api/queue/apiRequestQueueStore'
import type { PlannedNodeInstallMeta } from '../../utils/api/serviceCenterPlannedNodeInstall'
import { getNodeActions } from '../../utils/getNodeActions'
import { isSemiosGatewayType } from '../../utils/getNodeNetworkType'
import { DeviceList } from '../DeviceList/DeviceList'
import { DirectionButton } from '../DirectionButton/DirectionButton'
import { FailedRequestError } from '../FailedRequestError/FailedRequestError'
import { TargetPest } from '../Shared/TargetPest/TargetPest'

const checkPermission = (node: TPlannedNode | TPlannedGateway) => {
  if (isSemiosGatewayType(node)) {
    return selectedPropertyHasPermission({ permission: 'EDIT_SSC_INSTALL_PLANNED_GATEWAY' })
  } else {
    return selectedPropertyHasPermission({ permission: 'EDIT_SSC_INSTALL_PLANNED_NODE' })
  }
}

type PlannedNodeDetailProps = {
  node: TPlannedNode | TPlannedGateway
  onClose: () => void
}

export const PlannedNodeDetail: React.FC<PlannedNodeDetailProps> = ({ node, onClose }) => {
  const theme = useMantineTheme()
  const latLng = useMemo(() => transformCoordsToLatLng(JSON.parse(node.location) as Point), [node.location])
  const allFailedRequests = apiRequestQueueStore.useSelector(apiRequestQueueStore.selectors.getFailedRequests)
  const isWideScreen = useIsWideScreen()
  const menuCollapsed = layersNavigationMenuStore.useSelector((s) => s.menuCollapsed)
  const connected = networkStore.useSelector((s) => s.connectionStatus.connected)

  const nodeFailedRequests = useMemo(
    () => allFailedRequests.filter((req) => (req.meta as PlannedNodeInstallMeta).pseudonodeId === +node.id),
    [allFailedRequests, node],
  )

  const unsyncedNodeIdentifiers = apiRequestQueueStore.useSelector(
    apiRequestQueueStore.selectors.getUnsyncedNodeIdentifiers,
  )

  const isUnsynced = useMemo(() => unsyncedNodeIdentifiers.includes(node.id), [unsyncedNodeIdentifiers, node])

  const handleStartInstallation = () => {
    if (!(node as TPlannedNode)?.id) {
      throw new Error(`node is not a TPlannedNode: ${JSON.stringify(node)}`)
    }

    const location = JSON.parse(node.location) as GeoJSONPoint

    openNodeInstallationModal({
      payload: {
        location: location,
        pseudonodeId: node.id,
        nodeType: (node as TPlannedNode).nodeType,
        propertyId: (node as TPlannedNode).propertyId,
        name: node.name || undefined,
        managementType: (node as TPlannedNode).managementType as TManagementTypes,
        maintenanceOwnerId: (node as TPlannedNode).maintenanceOwnerId,
        devices: (node as TPlannedNode)?.devices,
      },
      localState: {
        isLocationConfirmed: false,
      },
    })
  }

  const nodeStatusText = getNodeStatusLabel(TNodeStatus.PLANNED)
  const nodeStatusColor = NODE_STATUS_COLORS[TNodeStatus.PLANNED]
  const equipmentType = !!node && getNodeEquipmentType(node.nodeType)

  let title = node.name || ''

  if (!node.name?.length) {
    title = translate.phrases.placeholder('Planned {{equipmentType}}', {
      equipmentType: equipmentType
        ? getEquipmentTypeLabel(equipmentType)
        : translate.phrases.placeholder('Node'),
    })
  }

  const handleOpenActionMenu = () => {
    const actions = getNodeActions(node, connected)

    openActionMenu(actions)
  }

  return (
    <MapCard
      title={title}
      onClose={onClose}
      latLng={latLng}
      cardCSS={menuCollapsed && !isWideScreen ? { zIndex: 10000 } : undefined}
      footer={
        <div css={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto' }}>
          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 15,
            }}
          >
            <Button variant="tertiary" size="lg" css={{ flex: '1 1 50%' }} onClick={handleOpenActionMenu}>
              {translate.phrases.placeholder('More Options')}
            </Button>

            <Button
              variant="primary"
              disabled={!checkPermission(node)}
              css={{ marginLeft: 10, flex: '1 1 50%' }}
              onClick={() => handleStartInstallation()}
            >
              {translate.phrases.placeholder('Install')}
            </Button>
          </div>
          <DirectionButton latLng={latLng} />
        </div>
      }
    >
      {isUnsynced && (
        <Alert
          icon={
            <span css={{ color: colors.yellow }}>
              <IconWarning />
            </span>
          }
          variant="light"
          styles={{ root: { backgroundColor: 'rgb(255, 249, 219)' } }}
        >
          {translate.phrases.placeholder('This node has unsynced changes')}
        </Alert>
      )}

      {nodeFailedRequests?.map((request) => (
        <FailedRequestError key={request.id} request={request} />
      ))}

      <div css={{ padding: '5px 20px 0' }}>
        <div
          css={{
            margin: '0 0 16px',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <Badge
            css={{
              color: theme.colors.midnight,
              backgroundColor: nodeStatusColor,
              fontSize: '12px',
              fontWeight: 500,
              textTransform: 'none',
            }}
          >
            {nodeStatusText}
          </Badge>
        </div>
        {TRAP_NODE_TYPES.includes((node as TPlannedNode).nodeType) && (
          <Grid css={{ fontSize: '14px', marginBottom: '8px' }}>
            <TargetPest />
          </Grid>
        )}

        {!!(node as TPlannedNode).devices && (
          <>
            <h4 css={{ marginBottom: 10, fontWeight: 500 }}>{translate.phrases.placeholder('Devices')}</h4>
            <DeviceList nodeDevices={(node as TPlannedNode).devices} />
          </>
        )}
      </div>
    </MapCard>
  )
}
